import React, { useState, useEffect, useMemo, useCallback } from 'react';
import CarCard from './components/CarCard';
import ExpenseTracker from './components/ExpenseTracker';
import Statistics from './components/Statistics';
import Settings from './components/Settings';
import CarSetupForm from './components/CarSetupForm';
import MaintenanceGrid from './components/MaintenanceGrid';
import RegionSelector from './components/RegionSelector';
import ServiceDashboard from './components/ServiceDashboard';
import ServiceStatistics from './components/ServiceStatistics';
import { Moon, Sun, Settings as SettingsIcon, X, Circle, Contrast, Droplet, Wind, FileText, CheckCircle, Shield, Wrench, Package, Disc, Cog } from 'lucide-react';
import { translations } from './translations';
const AdBanner = React.lazy(() => import('./components/Ads').then(module => ({ default: module.AdBanner })));
import WebAdBanner from './components/WebAdBanner';
import BottomNav from './components/BottomNav';
import ProfileSetup from './components/ProfileSetup';
import { useRevenueCat } from './hooks/useRevenueCat';
import { useStripe } from './hooks/useStripe';
import { supabase } from './lib/supabase';
import { useAuth } from './contexts/AuthContext';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { AdMob } from '@capacitor-community/admob';
import { InAppReview } from '@capacitor-community/in-app-review';
import './App.css';

// Feature flag: set to false before publishing to hide service registration from normal users
const SHOW_SERVICE_REGISTRATION = true;

// Cached once at module level — used in notification functions to match category names across all languages
const ALL_TRANSLATION_LANGS = Object.values(translations);

function App() {


  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme;
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    return 'light';
  });
  const [currency, setCurrency] = useState(() => localStorage.getItem('currency') || 'USD');
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'en');

  // Auto-select native currency when language changes
  const languageDefaultCurrency = {
    en: 'GBP', bg: 'EUR', zh: 'CNY', hi: 'INR', es: 'EUR',
    fr: 'EUR', ar: 'SAR', pt: 'EUR', ru: 'RUB', id: 'IDR',
    de: 'EUR', pl: 'PLN', sr: 'RSD', el: 'EUR', tr: 'TRY',
    no: 'NOK', sv: 'SEK',
  };
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    if (languageDefaultCurrency[lang]) {
      setCurrency(languageDefaultCurrency[lang]);
    }
  };

  const [distanceUnit, setDistanceUnit] = useState(() => localStorage.getItem('distanceUnit') || 'km');
  const [colorBlindMode, setColorBlindMode] = useState(() => localStorage.getItem('colorBlindMode') === 'true');

  const { user, loading: authLoading } = useAuth();

  // RevenueCat — manages isPro, purchase, and restore
  const {
    isPro,
    isProLoading,
    offerings,
    offeringsError,
    serviceProducts,
    purchasing,
    restoring,
    error: rcError,
    setError: setRcError,
    setIsPro,
    purchasePackage,
    purchaseServiceAccount,
    restorePurchases,
    presentPaywall,
    fetchOfferings,
  } = useRevenueCat(user?.id ?? null);

  // Stripe — web payment integration (only active on web platform)
  const isWeb = Capacitor.getPlatform() === 'web';
  const {
    purchasing: stripePurchasing,
    error: stripeError,
    setError: setStripeError,
    purchaseStripe,
    manageSubscription: stripeManageSubscription,
  } = useStripe(user?.id ?? null);

  // After Stripe redirects back with ?payment=success, sync pro status from Supabase
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      // Remove the query param from the URL without a reload
      window.history.replaceState({}, '', window.location.pathname);
      // Give the webhook ~3s to process, then re-sync from Supabase
      const timer = setTimeout(async () => {
        if (!user?.id) return;
        const { data } = await supabase
          .from('user_subscriptions')
          .select('is_pro')
          .eq('user_id', user.id)
          .single();
        if (data?.is_pro) {
          localStorage.setItem('isPro', 'true');
          localStorage.setItem('proSource', 'stripe');
          setIsPro(true);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Initialize AdMob on start
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const initAdMob = async () => {
        try {
          await AdMob.initialize({
            requestTrackingAuthorization: true,
            initializeForTesting: false,
          });

          // 25% chance of showing an app open ad for free users on startup
          if (!isPro && Math.random() < 0.25) {
            const timer = setTimeout(async () => {
              const { showAppOpenAd } = await import('./components/Ads');
              showAppOpenAd();
            }, 500); // Slight delay after init
            return () => clearTimeout(timer);
          }
        } catch (err) {
          console.warn('AdMob Init Error:', err);
        }
      };

      initAdMob();
    }
  }, [isPro]);

  const [categoryFilter, setCategoryFilter] = useState('All');
  const [showSettings, setShowSettings] = useState(false);
  const [showSetupForm, setShowSetupForm] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  // Never trust localStorage for account-type flags — always start neutral and let
  // Supabase set these once the session is confirmed. This prevents a logged-out
  // user from inheriting a previous session's 'Service' or 'Pro' status.
  const [isServiceAccount, setIsServiceAccount] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showPromoPopup, setShowPromoPopup] = useState(false);
  const [showMaintenanceGuidePopup, setShowMaintenanceGuidePopup] = useState(false);
  const [showRegionSelector, setShowRegionSelector] = useState(false); // driven by onboardingStep

  // Single sequential onboarding state machine
  // 'profile' → user sets name/color on first install
  // 'region'  → user picks their region next
  // 'done'    → normal app
  const [onboardingStep, setOnboardingStep] = useState(() => {
    if (!localStorage.getItem('profileName')) return 'profile';
    if (!localStorage.getItem('region')) return 'region';
    return 'done';
  });

  // Profile state — loaded from localStorage
  const [profileName, setProfileName] = useState(() => localStorage.getItem('profileName') || '');
  const [profileColor, setProfileColor] = useState(() => localStorage.getItem('profileColor') || '#5558FF');
  const [profilePhoto, setProfilePhoto] = useState(() => localStorage.getItem('profilePhoto') || null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#5558FF');
  const [editPhoto, setEditPhoto] = useState(null);
  const [editUsePhoto, setEditUsePhoto] = useState(false);
  const editFileRef = React.useRef();

  // Random Promo Popup logic on mount
  useEffect(() => {
    // Only show if user is NOT pro, hasn't dismissed a promo recently, and passes a random 20% chance check
    if (!isPro) {
      const lastPromoTime = localStorage.getItem('lastPromoTime');
      const now = Date.now();
      const twelveHours = 12 * 60 * 60 * 1000;

      // If no promo shown recently (or ever), give it a 25% chance to show
      if (!lastPromoTime || (now - parseInt(lastPromoTime)) > twelveHours) {
        if (Math.random() < 0.25) { // 25% chance to show per session
          const timer = setTimeout(() => {
            setShowPromoPopup(true);
            localStorage.setItem('lastPromoTime', now.toString());
          }, 4500); // Delay 4.5 seconds after app load
          return () => clearTimeout(timer);
        }
      }
    }
  }, [isPro]);

  // On app launch, check inspection / insurance dues for all cars.
  // Runs once when both cars and expenses are first available — not on every expense add.
  useEffect(() => {
    if (!isPro || expenses.length === 0 || cars.length === 0) return;
    for (const car of cars) {
      checkDateBasedServiceNotifications(car, expenses);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro]);

  const handleAddClick = () => {
    setActiveTab('add-expense');
  };

  const [cars, setCars] = useState([]);
  const [selectedCarId, setSelectedCarId] = useState(null);

  // Safe setter that also persists selection to localStorage and enforces locks
  const handleSelectCar = (id, carList = cars) => {
    const idx = carList.findIndex(c => c.id === id);
    if (idx !== -1 && (isPro || idx < 1)) {
      setSelectedCarId(id);
      localStorage.setItem('selectedCarId', id);
    } else if (!isPro && idx >= 1) {
      presentPaywall(); // Open paywall when a locked car is tapped
    }
  };

  const [expenses, setExpenses] = useState([]);
  const [serviceLogs, setServiceLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper to convert timestamp IDs to Postgres UUID
  const toUUID = (localId) => {
    if (String(localId).includes('-')) return localId;
    let hex = '';
    for (let i = 0; i < String(localId).length; i++) {
      hex += String(localId).charCodeAt(i).toString(16);
    }
    hex = hex.padEnd(32, '0').slice(0, 32);
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  };

  // Fetch verified service logs for user's cars
  useEffect(() => {
    if (!user || cars.length === 0) return;
    const fetchServiceLogs = async () => {
      try {
        const { data } = await supabase
          .from('service_maintenance_logs')
          .select('*')
          .in('car_id', cars.map(c => toUUID(c.id)));

        if (data && data.length > 0) {
          // Fetch business names for these service user IDs
          const serviceUserIds = [...new Set(data.map(log => log.service_user_id))];
          const { data: appData } = await supabase
            .from('service_applications')
            .select('user_id, business_name')
            .in('user_id', serviceUserIds)
            .eq('status', 'approved');

          const businessMap = {};
          if (appData) {
            appData.forEach(app => {
              businessMap[app.user_id] = app.business_name;
            });
          }

          const mapped = data.map(log => {
            const originalCar = cars.find(c => toUUID(c.id) === log.car_id);
            return {
              id: log.id,
              car_id: originalCar ? originalCar.id : log.car_id,
              title: log.description ? `${log.service_type} - ${log.description}` : log.service_type,
              amount: log.cost,
              category: log.service_type,
              date: log.service_date,
              mileage: log.mileage_at_service,
              notes: log.description || '',
              isServiceVerified: true,
              verifiedBy: businessMap[log.service_user_id] || 'Auto Service'
            };
          });
          setServiceLogs(mapped);
        } else {
          setServiceLogs([]); // Ensure we clear logs if all are deleted
        }
      } catch (err) {
        console.error('Error fetching service logs', err);
      }
    };

    fetchServiceLogs();

    const channel = supabase
      .channel('service_maintenance_logs_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'service_maintenance_logs' },
        (payload) => {
          fetchServiceLogs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, cars]);

  const allExpenses = useMemo(() => {
    return [...expenses, ...serviceLogs].sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at));
  }, [expenses, serviceLogs]);

  const t = translations[language];

  // Promo codes that unlock CarTrack+ — share these privately with users
  const PROMO_CODES = [
    'K7MN-P3QR-T8VW', 'B4CX-F2GH-J6KL', 'R9ST-U5WY-A3BZ', 'M2NP-Q8RT-V7XC',
    'G5HJ-K4LM-N6PQ', 'X3YZ-A7BC-D2EF', 'W6VU-T9SR-Q4PL', 'F8GH-J3KM-N5PQ',
    'C2DE-F7GH-J4KL', 'T6UV-W8XY-Z3AB', 'L4MN-P9QR-S7TU', 'H3JK-L6MN-P2QR',
    'Y7ZA-B4CD-E9FG', 'S5TU-V2WX-Y8ZA', 'N6PQ-R3ST-U7VW', 'A8BC-D4EF-G2HJ',
    'E3FG-H9JK-L5MN', 'V2WX-Y6ZA-B8CD', 'Q7RS-T4UV-W3XY', 'D5EF-G8HJ-K3LM',
    'Z4AB-C7DE-F6GH', 'U9VW-X5YZ-A2BC', 'J6KL-M3NP-Q9RT', 'P4QR-S8TU-V3WX',
    'B7CD-E4FG-H6JK', 'W2XY-Z9AB-C5DE', 'M8NP-Q6RS-T3UV', 'F4GH-J7KL-M9NP',
  ];

  const redeemPromoCode = async (code) => {
    const trimmed = code.trim().toUpperCase();

    // 1. Check if it's a valid code at all (client-side list)
    if (!PROMO_CODES.includes(trimmed)) {
      return { success: false, message: 'Invalid promo code.' };
    }

    // 2. Try to claim it in Supabase — INSERT will fail if already used (PRIMARY KEY violation)
    const { error } = await supabase
      .from('used_promo_codes')
      .insert({ code: trimmed });

    if (error) {
      // Postgres unique violation = code already claimed
      if (error.code === '23505') {
        return { success: false, message: 'This code has already been used.' };
      }
      // Any other DB error — still reject to be safe
      console.error('Promo redeem error:', error);
      return { success: false, message: 'Could not verify code. Please try again.' };
    }

    // 3. Code is valid and freshly claimed — unlock CarTrack+
    localStorage.setItem('isPro', 'true');
    localStorage.setItem('proSource', 'promo');

    // 4. If user is logged in, save pro status to cloud so it restores on other devices
    if (user?.id) {
      await supabase
        .from('user_subscriptions')
        .upsert({ user_id: user.id, is_pro: true, pro_source: 'promo', updated_at: new Date().toISOString() },
          { onConflict: 'user_id' });
    }

    // 5. Update React state directly
    setIsPro(true);
    return { success: true };
  };

  // Save preferences to localStorage

  // Request Local Notification permissions and schedule automatic reminders
  useEffect(() => {
    const setupLocalNotifications = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          let permStatus = await LocalNotifications.checkPermissions();
          if (permStatus.display === 'prompt') {
            permStatus = await LocalNotifications.requestPermissions();
          }
          if (permStatus.display !== 'granted') {
            console.log('User denied local notification permission');
            return;
          }

          // Store the install weekday once (JS 0=Sun→Capacitor 1, JS 6=Sat→Capacitor 7)
          if (!localStorage.getItem('installWeekday')) {
            localStorage.setItem('installWeekday', String(new Date().getDay() + 1));
          }
          const installWeekday = parseInt(localStorage.getItem('installWeekday'), 10);

          // Clear any existing notifications to avoid duplicates when reloading the app
          await LocalNotifications.cancel({ notifications: [1, 2, 3, 4, 5, 6, 7, 8, 9].map(id => ({ id })) });

          // Capacitor weekday mapping: 1=Sunday, 2=Monday, 3=Tue, 4=Wed, 5=Thu, 6=Fri, 7=Saturday
          const driveSafeNotification = {
            title: 'Drive Safe',
            body: "Check your tires pressure before driving.",
            smallIcon: 'ic_stat_icon_config_sample',
            sound: null,
          };

          const mileageNotification = {
            title: 'Update Your Mileage',
            body: "Don't forget to manually add the miles on your car to keep your stats accurate!",
            smallIcon: 'ic_stat_icon_config_sample',
            sound: null,
          };

          const oilCheckNotification = {
            title: 'Oil Level Reminder',
            body: "It's been a while! Check your oil level before your next big adventure.",
            smallIcon: 'ic_stat_icon_config_sample',
            sound: null,
          };

          const expenseSummaryNotification = {
            title: '📊 Weekly Expense Summary',
            body: "Check your CarTrack to see how much you spent on your car this week!",
            smallIcon: 'ic_stat_icon_config_sample',
            sound: null,
          };

          const coolantNotification = {
            title: '💧 Coolant & Fluid Check',
            body: "Monthly reminder: check your coolant, washer fluid, and brake fluid levels.",
            smallIcon: 'ic_stat_icon_config_sample',
            sound: null,
          };

          const notificationsToSchedule = [
            // Drive Safe (Mon, Wed, Fri, Sat)
            { ...driveSafeNotification, id: 1, schedule: { on: { weekday: 2, hour: 10, minute: 0 } } },
            { ...driveSafeNotification, id: 2, schedule: { on: { weekday: 4, hour: 10, minute: 0 } } },
            { ...driveSafeNotification, id: 3, schedule: { on: { weekday: 6, hour: 10, minute: 0 } } },
            { ...driveSafeNotification, id: 4, schedule: { on: { weekday: 7, hour: 10, minute: 0 } } },

            // Oil Check (Sunday at 10:00 AM)
            { ...oilCheckNotification, id: 7, schedule: { on: { weekday: 1, hour: 10, minute: 0 } } },

            // Weekly Expense Summary — every week on the day the app was installed, 7:00 PM
            { ...expenseSummaryNotification, id: 8, schedule: { on: { weekday: installWeekday, hour: 19, minute: 0 } } },

            // Coolant & Fluid Check — every month on the 15th at 10:00 AM
            { ...coolantNotification, id: 9, schedule: { on: { day: 15, hour: 10, minute: 0 } } },
          ];

          // Update Mileage reminders (Only for FREE users) — Friday only
          if (!isPro) {
            notificationsToSchedule.push(
              { ...mileageNotification, id: 5, schedule: { on: { weekday: 6, hour: 18, minute: 0 } } } // Friday 6:00 PM
            );
          }

          await LocalNotifications.schedule({
            notifications: notificationsToSchedule
          });

          console.log('Local maintenance reminders scheduled successfully.');
        } catch (error) {
          console.error('Local notification setup failed.', error);
        }
      }
    };
    setupLocalNotifications();
  }, []);


  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem('currency', currency);
  }, [currency]);

  useEffect(() => {
    localStorage.setItem('distanceUnit', distanceUnit);
  }, [distanceUnit]);

  useEffect(() => {
    localStorage.setItem('colorBlindMode', colorBlindMode);
  }, [colorBlindMode]);

  // Sync CarTrack+ status from Supabase (source of truth) when the user session resolves.
  // Runs only after auth has finished loading to avoid race conditions with the
  // initial null→user transition that previously caused infinite reloads.
  // Uses setIsPro() directly — NO window.location.reload() which caused the loop.
  useEffect(() => {
    // Wait until Supabase auth has fully resolved before acting
    if (authLoading) return;

    if (!user) {
      // Auth resolved and no user — we're definitely a guest. setIsPro(false) is
      // already handled by useRevenueCat (userId=null branch). localStorage is
      // cleared by AuthContext on intentional sign-out. Nothing to do here.
      return;
    }

    const syncProFromCloud = async () => {
      const { data } = await supabase
        .from('user_subscriptions')
        .select('is_pro, pro_source')
        .eq('user_id', user.id)
        .single();

      const cloudIsPro = data?.is_pro === true;
      const cloudSource = data?.pro_source || 'cloud_restore';

      // If Supabase says Pro, ALWAYS trust the cloud — this is the source of truth.
      // This handles the case where localStorage was wiped by an app update but the
      // user still has an active subscription saved in the database.
      if (cloudIsPro) {
        localStorage.setItem('isPro', 'true');
        localStorage.setItem('proSource', cloudSource);
        setIsPro(true);
        return;
      }

      // Cloud says NOT Pro. If there's a locally-held promo unlock that hasn't been
      // synced yet (e.g. offline redeem), keep it — don't strip it.
      const isPromoLocal = localStorage.getItem('proSource') === 'promo';
      if (isPromoLocal) return;

      // Otherwise, Supabase is the authority — revoke pro status.
      localStorage.removeItem('isPro');
      localStorage.removeItem('proSource');
      setIsPro(false);
    };
    syncProFromCloud();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);


  // Check if user is a service account
  useEffect(() => {
    if (!user) {
      setIsServiceAccount(false);
      localStorage.removeItem('isServiceAccount');
      return;
    }
    const checkServiceRole = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();
      const isService = data?.role === 'service';
      setIsServiceAccount(isService);
      if (isService) {
        localStorage.setItem('isServiceAccount', 'true');
        setActiveTab(prev => prev === 'dashboard' ? 'service' : prev);
      } else {
        localStorage.removeItem('isServiceAccount');
      }
    };
    checkServiceRole();
  }, [user]);

  // Handle region selection
  const handleRegionSelect = (code) => {
    localStorage.setItem('region', code);
    setShowRegionSelector(false);
    // Save to profile if logged in
    if (user?.id) {
      supabase.from('profiles').update({ region: code }).eq('id', user.id);
    }
  };

  // Load data from localStorage
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = () => {
    try {
      setLoading(true);

      // Load cars from localStorage
      const savedCars = localStorage.getItem('cars');
      const carsData = savedCars ? JSON.parse(savedCars) : [];

      if (carsData.length > 0) {
        setCars(carsData);

        let initialSelectedId = carsData[0].id;
        const savedSelectedId = localStorage.getItem('selectedCarId'); // Check if there's a saved selection

        if (savedSelectedId) {
          const savedIndex = carsData.findIndex(c => c.id === savedSelectedId);
          // If the saved car exists AND it's not locked (or they are Pro)
          if (savedIndex !== -1 && (isPro || savedIndex < 1)) {
            initialSelectedId = savedSelectedId;
          }
        }

        handleSelectCar(initialSelectedId, carsData);

        // Load expenses from localStorage
        const savedExpenses = localStorage.getItem('expenses');
        const expensesData = savedExpenses ? JSON.parse(savedExpenses) : [];
        setExpenses(expensesData);
      } else {
        // No cars yet — go straight to the main menu (empty state)
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const THEME_CYCLE = ['light', 'dark'];
  const toggleTheme = () => {
    setTheme(prev => {
      const idx = THEME_CYCLE.indexOf(prev);
      return THEME_CYCLE[(idx + 1) % THEME_CYCLE.length];
    });
  };

  const themeIcon = () => {
    if (theme === 'light') return <Moon size={20} />;
    return <Sun size={20} />;
  };

  // Service category → km interval mapping (mirrors MaintenanceGrid)
  const SERVICE_INTERVALS = [
    { id: 'oil', kmInterval: 12000, keys: (tr) => tr.categories?.oilChange },
    { id: 'oilFilter', kmInterval: 12000, keys: (tr) => tr.categories?.oilFilter },
    { id: 'airFilter', kmInterval: 30000, keys: (tr) => tr.categories?.airFilter },
    { id: 'cabinFilter', kmInterval: 20000, keys: (tr) => tr.categories?.cabinFilter },
  ];

  // Build a lookup: category display name → service id
  const buildCategoryToServiceId = () => {
    const map = {};
    const langs = Object.values(translations);
    SERVICE_INTERVALS.forEach(svc => {
      langs.forEach(lang => {
        const name = svc.keys(lang);
        if (name) map[name] = svc.id;
      });
    });
    return map;
  };

  const checkServiceDueNotifications = async (car, allExpenses) => {
    if (!isPro || !Capacitor.isNativePlatform()) return;
    const currentKm = (parseInt(car.mileage) || 0) + (car.total_mileage_km || 0);
    const langs = ALL_TRANSLATION_LANGS;
    // Car index makes IDs unique across cars (each car gets a 1000-wide ID block)
    const carIdx = cars.findIndex(c => c.id === car.id);

    for (const svc of SERVICE_INTERVALS) {
      const svcIdx = SERVICE_INTERVALS.indexOf(svc);
      // Collect all names this category can have across languages
      const categoryKeys = langs.map(l => svc.keys(l)).filter(Boolean);
      const history = allExpenses
        .filter(e => e.car_id === car.id && categoryKeys.includes(e.category) && e.mileage != null)
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      if (history.length === 0) continue;

      const lastKm = parseInt(history[0].mileage) || 0;
      const kmLeft = svc.kmInterval - (currentKm - lastKm);

      const storageKey = `serviceDue_${car.id}_${svc.id}`;
      const fired = JSON.parse(localStorage.getItem(storageKey) || '[]');

      const thresholds = [
        { label: '4000', km: 4000, title: `🔧 ${history[0].category} Due Soon`, body: `${car.name}: about 4,000 km left — we recommend a ${history[0].category}.` },
        { label: '2000', km: 2000, title: `🔧 ${history[0].category} Due Soon`, body: `${car.name}: about 2,000 km until your next ${history[0].category}.` },
        { label: '500', km: 500, title: `⚠️ ${history[0].category} Due Very Soon`, body: `${car.name}: only ~500 km left until your ${history[0].category} is due!` },
        { label: 'overdue', km: 0, title: `🚨 ${history[0].category} Overdue!`, body: `${car.name}: your ${history[0].category} is overdue — service it soon!` },
      ];

      for (let i = 0; i < thresholds.length; i++) {
        const t = thresholds[i];
        const crossed = t.label === 'overdue' ? kmLeft < 0 : (kmLeft <= t.km && kmLeft >= 0);
        if (crossed && !fired.includes(t.label)) {
          try {
            await LocalNotifications.schedule({
              notifications: [{
                // 200000 + carIdx*1000 + svcIdx*10 + thresholdIdx — unique per car, service, threshold
                id: 200000 + carIdx * 1000 + svcIdx * 10 + i,
                title: t.title,
                body: t.body,
                smallIcon: 'ic_stat_icon_config_sample',
                sound: null,
                schedule: { at: new Date(Date.now() + (i + 1) * 1500) },
              }]
            });
            localStorage.setItem(storageKey, JSON.stringify([...fired, t.label]));
          } catch (e) {
            console.warn('Service due notification failed:', e);
          }
        }
      }
    }
  };

  // Date-based notifications for Car Inspection and Insurance
  // Fires: 30 days before (1 month), 7 days before (1 week), and when overdue
  const DATE_SERVICES = [
    {
      id: 'inspection',
      keys: (lang) => lang.categories?.carInspection,
      notifIdBase: 30000,
    },
    {
      id: 'insurance',
      keys: (lang) => lang.categories?.insurance,
      notifIdBase: 30010,
    },
  ];

  const checkDateBasedServiceNotifications = async (car, allExpenses) => {
    if (!isPro || !Capacitor.isNativePlatform()) return;
    const langs = ALL_TRANSLATION_LANGS;
    // Car index makes IDs unique across cars (each car gets a 100-wide block per service type)
    const carIdx = cars.findIndex(c => c.id === car.id);

    for (let svcTypeIdx = 0; svcTypeIdx < DATE_SERVICES.length; svcTypeIdx++) {
      const svc = DATE_SERVICES[svcTypeIdx];
      const categoryKeys = langs.map(l => svc.keys(l)).filter(Boolean);
      const history = allExpenses
        .filter(e => e.car_id === car.id && categoryKeys.includes(e.category))
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      if (history.length === 0) continue;

      const latest = history[0];
      const categoryLabel = latest.category;

      // Determine due date: use explicit expiryDate if set, otherwise add 12 months to expense date
      let dueDate;
      if (latest.expiryDate) {
        dueDate = new Date(latest.expiryDate);
      } else {
        const base = new Date(latest.date);
        dueDate = new Date(base.setMonth(base.getMonth() + 12));
      }

      const daysLeft = Math.ceil((dueDate - new Date()) / (1000 * 60 * 60 * 24));

      const storageKey = `dateDue_${car.id}_${svc.id}`;
      const fired = JSON.parse(localStorage.getItem(storageKey) || '[]');

      const thresholds = [
        {
          label: '30d',
          check: daysLeft <= 30 && daysLeft > 7,
          title: `📅 ${categoryLabel} Due Soon`,
          body: `${car.name}: your ${categoryLabel} expires in about 1 month. Time to renew!`,
        },
        {
          label: '7d',
          check: daysLeft <= 7 && daysLeft >= 0,
          title: `⚠️ ${categoryLabel} Expiring This Week`,
          body: `${car.name}: only ${daysLeft} day${daysLeft !== 1 ? 's' : ''} until your ${categoryLabel} expires!`,
        },
        {
          label: 'overdue',
          check: daysLeft < 0,
          title: `🚨 ${categoryLabel} Expired!`,
          body: `${car.name}: your ${categoryLabel} has expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago. Renew immediately!`,
        },
      ];

      for (let i = 0; i < thresholds.length; i++) {
        const thr = thresholds[i];
        if (thr.check && !fired.includes(thr.label)) {
          try {
            await LocalNotifications.schedule({
              notifications: [{
                // 300000 + carIdx*100 + svcTypeIdx*10 + thresholdIdx — unique per car, service type, threshold
                id: 300000 + carIdx * 100 + svcTypeIdx * 10 + i,
                title: thr.title,
                body: thr.body,
                smallIcon: 'ic_stat_icon_config_sample',
                sound: null,
                schedule: { at: new Date(Date.now() + (i + 1) * 1500) },
              }]
            });
            localStorage.setItem(storageKey, JSON.stringify([...fired, thr.label]));
          } catch (e) {
            console.warn('Date-based service notification failed:', e);
          }
        }
      }
    }
  };

  const addExpense = async (expense) => {
    try {
      const newExpense = {
        ...expense,
        id: Date.now().toString(),
        created_at: new Date().toISOString()
      };
      const updatedExpenses = [newExpense, ...expenses];
      setExpenses(updatedExpenses);
      localStorage.setItem('expenses', JSON.stringify(updatedExpenses));

      // Trigger Maintenance Guide Popup on first expense
      // Delayed 2.5s so it doesn't clash with the Approved stamp animation (~2.2s)
      if (!localStorage.getItem('hasShownMaintenanceGuidePopup')) {
        localStorage.setItem('hasShownMaintenanceGuidePopup', 'true');
        setTimeout(() => setShowMaintenanceGuidePopup(true), 2500);
      }

      // Trigger in-app review after 5th expense logged (once only)
      if (
        Capacitor.isNativePlatform() &&
        !localStorage.getItem('ratingPrompted') &&
        updatedExpenses.length >= 5
      ) {
        try {
          await InAppReview.requestReview();
          localStorage.setItem('ratingPrompted', 'true');
        } catch (e) {
          console.warn('In-app review failed:', e);
        }
      }

      // Reset service-due tracking for this category so notifications re-arm after next service
      if (isPro && expense.car_id) {
        const catToId = buildCategoryToServiceId();
        const svcId = catToId[expense.category];
        if (svcId) {
          localStorage.removeItem(`serviceDue_${expense.car_id}_${svcId}`);
        }

        // Reset + re-check date-based notifications for inspection / insurance when renewed
        const langs = ALL_TRANSLATION_LANGS;
        for (const svc of DATE_SERVICES) {
          const categoryKeys = langs.map(l => svc.keys(l)).filter(Boolean);
          if (categoryKeys.includes(expense.category)) {
            localStorage.removeItem(`dateDue_${expense.car_id}_${svc.id}`);
            const car = cars.find(c => c.id === expense.car_id);
            if (car) {
              // Use already-built updatedExpenses — no need to rebuild with a new duplicate object
              checkDateBasedServiceNotifications(car, updatedExpenses);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error adding expense:', error);
    }
  };

  const deleteExpense = (id) => {
    try {
      const updatedExpenses = expenses.filter(e => e.id !== id);
      setExpenses(updatedExpenses);
      localStorage.setItem('expenses', JSON.stringify(updatedExpenses));
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const updateCarName = (carId, newName) => {
    try {
      const updatedCars = cars.map(car =>
        car.id === carId ? { ...car, name: newName, updated_at: new Date().toISOString() } : car
      );
      setCars(updatedCars);
      localStorage.setItem('cars', JSON.stringify(updatedCars));
    } catch (error) {
      console.error('Error updating car name:', error);
    }
  };

  const addCar = () => {
    setEditingCar(null);
    setShowSetupForm(true);
  };

  const editCar = (car) => {
    setEditingCar(car);
    setShowSetupForm(true);
  };

  const handleSetupComplete = (carData) => {
    if (!carData) {
      setShowSetupForm(false);
      setEditingCar(null);
      return;
    }

    let updatedCars;

    if (editingCar) {
      // Update existing car
      updatedCars = cars.map(c => c.id === carData.id ? carData : c);
    } else {
      // Add new car
      updatedCars = [...cars, carData];
    }

    setCars(updatedCars);

    // Only auto-select the new/edited car if it's unlocked (pro, or index < 1)
    const newCarIndex = updatedCars.findIndex(c => c.id === carData.id);
    if (isPro || newCarIndex < 1) {
      handleSelectCar(carData.id, updatedCars);
    }
    localStorage.setItem('cars', JSON.stringify(updatedCars));
    setShowSetupForm(false);
    setEditingCar(null);
  };

  const updateCarImage = (carId, type, base64Image) => {
    const updatedCars = cars.map(c =>
      c.id === carId
        ? { ...c, [type === 'bg' ? 'bgImage' : 'gaugeImage']: base64Image }
        : c
    );
    setCars(updatedCars);
    localStorage.setItem('cars', JSON.stringify(updatedCars));
  };

  const updateCarBgPosition = (carId, newPositionPercent) => {
    const updatedCars = cars.map(c =>
      c.id === carId ? { ...c, bgPosition: newPositionPercent } : c
    );
    setCars(updatedCars);
    localStorage.setItem('cars', JSON.stringify(updatedCars));
  };

  const deleteCar = (carId) => {
    if (cars.length <= 1) return; // safety guard — can't delete last car
    const updatedCars = cars.filter(c => c.id !== carId);
    // Remove all expenses for this car
    const updatedExpenses = expenses.filter(e => e.car_id !== carId);

    // Select another car (ensuring we don't fallback to a locked car if non-Pro)
    let nextCarIndex = 0;
    if (selectedCarId === carId) {
      // We deleted the currently selected car, find a new valid one
      nextCarIndex = updatedCars.findIndex(c => isPro || updatedCars.indexOf(c) < 1);
      if (nextCarIndex === -1) nextCarIndex = 0; // Fallback to 0 just in case
    } else {
      // Selected car wasn't deleted, see if its new index is still valid
      const currentIndex = updatedCars.findIndex(c => c.id === selectedCarId);
      if (currentIndex !== -1 && (isPro || currentIndex < 1)) {
        nextCarIndex = currentIndex;
      } else {
        nextCarIndex = 0;
      }
    }

    // Clean up all car-specific localStorage keys so they don't pollute future cars
    localStorage.removeItem(`mileageUpdateCount_${carId}`);
    localStorage.removeItem(`milestonesHit_${carId}`);
    localStorage.removeItem(`gps_trips_${carId}`);
    for (const svc of SERVICE_INTERVALS) {
      localStorage.removeItem(`serviceDue_${carId}_${svc.id}`);
    }
    for (const svc of DATE_SERVICES) {
      localStorage.removeItem(`dateDue_${carId}_${svc.id}`);
    }

    const nextCar = updatedCars[nextCarIndex];
    setCars(updatedCars);
    setExpenses(updatedExpenses);
    handleSelectCar(nextCar.id, updatedCars);
    localStorage.setItem('cars', JSON.stringify(updatedCars));
    localStorage.setItem('expenses', JSON.stringify(updatedExpenses));
  };

  // Mileage milestones — fires a one-time notification each time a car crosses one
  const MILEAGE_MILESTONES = [1000, 5000, 10000, 25000, 50000, 100000, 200000, 500000, 1000000];

  const checkMilestonesAndNotify = async (carId, carName, totalKm) => {
    if (!isPro || !Capacitor.isNativePlatform()) return;
    const key = `milestonesHit_${carId}`;
    const alreadyHit = JSON.parse(localStorage.getItem(key) || '[]');
    const newHits = MILEAGE_MILESTONES.filter(m => totalKm >= m && !alreadyHit.includes(m));
    if (newHits.length === 0) return;

    const fmt = (km) => km >= 1000000 ? `${km / 1000000}M` : `${km / 1000}k`;
    // Car index makes IDs unique across cars (each car gets a 10000-wide block)
    const carIdx = Math.max(0, cars.findIndex(c => c.id === carId));

    for (let i = 0; i < newHits.length; i++) {
      const m = newHits[i];
      const milestoneIdx = MILEAGE_MILESTONES.indexOf(m);
      try {
        await LocalNotifications.schedule({
          notifications: [{
            // 400000 + carIdx*10000 + milestoneArrayIdx — unique per car and milestone
            id: 400000 + carIdx * 10000 + milestoneIdx,
            title: `🏁 ${fmt(m)} km Milestone!`,
            body: `${carName} just hit ${fmt(m)} km — congrats on the journey!`,
            smallIcon: 'ic_stat_icon_config_sample',
            sound: null,
            schedule: { at: new Date(Date.now() + (i + 1) * 2000) },
          }]
        });
      } catch (e) {
        console.warn('Milestone notification failed:', e);
      }
    }
    localStorage.setItem(key, JSON.stringify([...alreadyHit, ...newHits]));
  };

  const updateMileage = (carId, newMileage) => {
    const updatedCars = cars.map(c => c.id === carId ? { ...c, mileage: newMileage } : c);
    setCars(updatedCars);
    localStorage.setItem('cars', JSON.stringify(updatedCars));
    const car = cars.find(c => c.id === carId);
    if (car) {
      // Track how many times mileage has been manually updated for this car.
      // Milestone notifications only fire after the 2nd update (skip first-add and first-log).
      const countKey = `mileageUpdateCount_${carId}`;
      const updateCount = (parseInt(localStorage.getItem(countKey)) || 0) + 1;
      localStorage.setItem(countKey, String(updateCount));

      const totalKm = parseInt(newMileage) || 0;
      // Milestones are GPS-only — do not call checkMilestonesAndNotify here
      checkServiceDueNotifications({ ...car, mileage: newMileage }, expenses);
    }
  };

  const toggleGauge = (carId) => {
    const updatedCars = cars.map(c => c.id === carId ? { ...c, hideGauge: !c.hideGauge } : c);
    setCars(updatedCars);
    localStorage.setItem('cars', JSON.stringify(updatedCars));
  };

  // Canonical total odometer in km — used by MaintenanceGrid and ExpenseTracker.
  const selectedCar = cars.find(c => c.id === selectedCarId);
  const currentMileageKm = selectedCar
    ? (parseInt(selectedCar.mileage) || 0) + Math.round(selectedCar.total_mileage_km || 0)
    : 0;


  // Show loading screen while loading data
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#738ADB',
        gap: '0',
        overflow: 'hidden',
        position: 'relative',
      }}>
        {/* Decorative blobs */}
        <div style={{
          position: 'absolute', width: '300px', height: '300px',
          background: 'rgba(255,255,255,0.05)', borderRadius: '50%',
          top: '-80px', right: '-80px',
        }} />
        <div style={{
          position: 'absolute', width: '200px', height: '200px',
          background: 'rgba(255,255,255,0.05)', borderRadius: '50%',
          bottom: '-60px', left: '-60px',
        }} />

        {/* Pulse ring */}
        <div style={{
          position: 'relative', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          marginBottom: '28px',
        }}>
          <div style={{
            position: 'absolute',
            width: '120px', height: '120px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            animation: 'ctPulse 1.6s ease-out infinite',
          }} />
          {/* App icon */}
          <div style={{
            width: '88px', height: '88px', borderRadius: '22px',
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            animation: 'ctFadeIn 0.5s ease-out',
          }}>
            <svg viewBox="0 0 512 512" width="56" height="56">
              <g transform="translate(100, 132) scale(1.2)" stroke="white" strokeWidth="36" strokeLinecap="round" strokeLinejoin="round" fill="none">
                <path d="M 30,120 L 30,80 Q 30,50 55,48 L 100,45 Q 115,45 130,58 L 165,85 Q 175,93 190,93 L 210,93 Q 225,93 230,108 L 230,135 Q 230,145 220,145 L 190,145 M 30,120 Q 30,145 40,145 L 70,145 M 95,145 L 165,145" />
                <circle cx="82" cy="145" r="16" />
                <circle cx="82" cy="145" r="4" fill="white" stroke="none" />
                <circle cx="178" cy="145" r="16" />
                <circle cx="178" cy="145" r="4" fill="white" stroke="none" />
              </g>
            </svg>
          </div>
        </div>

        {/* App name */}
        <h1 style={{
          color: 'white', fontSize: '2.2rem', fontWeight: '800',
          margin: '0 0 6px 0', letterSpacing: '-0.5px',
          animation: 'ctFadeIn 0.6s ease-out 0.1s both',
        }}>
          CarTrack
        </h1>
        <p style={{
          color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem',
          margin: '0 0 40px 0', fontWeight: '400',
          animation: 'ctFadeIn 0.6s ease-out 0.2s both',
        }}>
          Your car. Your data.
        </p>

        {/* Loading bar */}
        <div style={{
          width: '140px', height: '3px',
          background: 'rgba(255,255,255,0.2)',
          borderRadius: '99px', overflow: 'hidden',
          animation: 'ctFadeIn 0.6s ease-out 0.3s both',
        }}>
          <div style={{
            height: '100%',
            background: 'white',
            borderRadius: '99px',
            animation: 'ctBar 1.2s ease-in-out infinite',
          }} />
        </div>

        <style>{`
          @keyframes ctPulse {
            0% { transform: scale(0.9); opacity: 0.7; }
            70% { transform: scale(1.4); opacity: 0; }
            100% { transform: scale(1.4); opacity: 0; }
          }
          @keyframes ctFadeIn {
            from { opacity: 0; transform: translateY(12px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes ctBar {
            0%   { width: 0%;   margin-left: 0%; }
            50%  { width: 60%;  margin-left: 20%; }
            100% { width: 0%;   margin-left: 100%; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="app-mobile">
      {/* Fixed Header - Hidden on Dashboard */}
      {activeTab !== 'dashboard' && (
        <header className="mobile-header">
          <h1 className="app-logo" style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            color: 'var(--accent-color)',
            ...(theme === 'white-contrast' ? { background: 'none', WebkitTextFillColor: 'black', color: 'black' } : {})
          }}>
            {isServiceAccount ? 'CarTrack Service' : (!isProLoading && isPro ? 'CarTrack+' : t.appName)}
          </h1>
          <div className="header-actions">
            <button className="icon-btn" onClick={toggleTheme} aria-label="Toggle theme">
              {themeIcon()}
            </button>
            <button className="icon-btn" onClick={() => setShowSettings(true)} aria-label="Settings">
              <SettingsIcon size={20} />
            </button>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className={`mobile-content${activeTab === 'dashboard' ? ' mobile-content--dashboard' : ''}`} style={{ paddingBottom: '90px' }}>
        {activeTab === 'dashboard' && (
          <div className="dashboard-layout" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '0 0.5rem' }}>
            {/* User Profile Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0 1rem 0' }}>
              <button
                onClick={() => {
                  setEditName(profileName);
                  setEditColor(profileColor);
                  setEditPhoto(profilePhoto);
                  setEditUsePhoto(!!profilePhoto);
                  setShowEditProfile(true);
                }}
                aria-label="Edit profile"
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                }}
              >
                <div style={{
                  width: '44px', height: '44px', borderRadius: '50%',
                  background: profilePhoto ? 'transparent' : (profileColor || 'var(--ui-accent)'),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: profileColor === '#FAF105' ? '#1a1a1a' : 'white', fontWeight: 700, fontSize: '1.2rem',
                  overflow: 'hidden',
                  boxShadow: `0 4px 12px ${profileColor || '#5558FF'}55`,
                  border: `2px solid ${profileColor || 'var(--accent-color)'}`,
                  flexShrink: 0,
                }}>
                  {profilePhoto
                    ? <img src={profilePhoto} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : (profileName ? profileName[0].toUpperCase() : (user?.email ? user.email[0].toUpperCase() : 'U'))
                  }
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {profileName || (user?.email ? user.email.split('@')[0] : (t?.myProfile || 'My Profile'))}
                </div>
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  onClick={toggleTheme}
                  aria-label="Toggle theme"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    transition: 'all 0.2s',
                    flexShrink: 0,
                  }}
                >
                  {themeIcon()}
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  aria-label="Settings"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    transition: 'all 0.2s',
                    flexShrink: 0,
                  }}
                >
                  <SettingsIcon size={20} />
                </button>
              </div>
            </div>

            <CarCard
              cars={cars}
              selectedCarId={selectedCarId}
              onSelectCar={handleSelectCar}
              onUpdateCarName={updateCarName}
              onAddCar={addCar}
              onEditCar={editCar}
              onDeleteCar={deleteCar}
              onUpdateMileage={updateMileage}
              onUpdateCarImage={updateCarImage}
              onUpdateCarBgPosition={updateCarBgPosition}
              onToggleGauge={toggleGauge}
              language={language}
              isPro={isPro}
              onUpgradeClick={() => presentPaywall()}
              onPresentPaywall={presentPaywall}
              distanceUnit={distanceUnit}
            />

            <MaintenanceGrid
              t={t}
              expenses={allExpenses.filter(e => e.car_id === selectedCarId)}
              car={cars.find(c => c.id === selectedCarId)}
              distanceUnit={distanceUnit}
              isPro={isPro}
              onProClick={() => presentPaywall()}
              currentMileageKm={currentMileageKm}
            />

            {/* Recent Changes List */}
            <div style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>
                {t?.recentChanges || 'Recent changes'}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {allExpenses.filter(e => e.car_id === selectedCarId).slice(0, 4).map((entry, idx) => {
                  const matchedKey = Object.keys(t.categories).find(key =>
                    Object.values(translations).some(lang => lang.categories && lang.categories[key] === entry.category)
                  );
                  const displayCategory = matchedKey ? t.categories[matchedKey] : entry.category;

                  const getCatIcon = (cat) => {
                    if (!cat) return <Package size={18} />;
                    if ([t.categories.oilChange].includes(cat)) return <Droplet size={18} />;
                    if ([t.categories.filter, t.categories.oilFilter, t.categories.fuelFilter, t.categories.cabinFilter, t.categories.transmissionFilter, t.categories.airFilter].includes(cat)) return <Wind size={18} />;
                    if ([t.categories.insurance].includes(cat)) return <Shield size={18} />;
                    if ([t.categories.carInspection].includes(cat)) return <CheckCircle size={18} />;
                    if ([t.categories.repairs].includes(cat)) return <Wrench size={18} />;
                    if ([t.categories.maintenance].includes(cat)) return <Cog size={18} />;
                    if ([t.categories.brakes, t.categories.brakePads, t.categories.brakeRotors].includes(cat)) return <Disc size={18} />;
                    return <Package size={18} />;
                  };

                  return (
                    <div key={entry.id || idx} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '1rem', background: 'var(--bg-card)', borderRadius: '16px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                          width: '40px', height: '40px', borderRadius: '50%',
                          background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'var(--text-secondary)'
                        }}>
                          {getCatIcon(displayCategory)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                            {displayCategory}
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.1rem' }}>
                            {entry.date ? new Date(entry.date).toLocaleDateString() : (t?.recent || 'Recent')}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, color: 'var(--text-secondary)', fontSize: '1rem' }}>
                        {entry.amount ? `-${currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency} ${Number(entry.amount).toFixed(2)}` : 'Logged'}
                      </div>
                    </div>
                  );
                })}
                {allExpenses.filter(e => e.car_id === selectedCarId).length === 0 && (
                  <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    {t?.noRecentChanges || 'No recent changes'}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'service' && isServiceAccount && (
          <ServiceDashboard />
        )}

        {activeTab === 'service-stats' && isServiceAccount && (
          <div style={{ padding: '0 1rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.3rem', fontWeight: 700 }}>{t?.revenueStats || 'Revenue Stats'}</h2>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{t?.revenueStatsDesc || 'View your service revenue analytics'}</p>
            </div>
            <ServiceStatistics user={user} language={language} />
          </div>
        )}

        {(activeTab === 'expenses' || activeTab === 'add-expense') && (() => {
          const carExpenses = allExpenses.filter(e => e.car_id === selectedCarId);
          const hasExpenses = carExpenses.length > 0;
          
          if (activeTab === 'expenses' && !hasExpenses) {
            return (
              <div style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
                pointerEvents: 'none',
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '1.05rem',
                  lineHeight: '1.6',
                  color: 'var(--text-secondary)',
                  textAlign: 'center',
                  maxWidth: '260px',
                }}>
                  {t?.addFirstExpensePrompt || 'Add your first expense using the + in the bottom navigation menu.'}
                </p>
              </div>
            );
          }

          return (
            <>
              {activeTab === 'expenses' && (
                <Statistics
                  expenses={carExpenses}
                  categoryFilter={categoryFilter}
                  language={language}
                  colorBlindMode={colorBlindMode}
                />
              )}

              <ExpenseTracker
                expenses={carExpenses}
                onAddExpense={addExpense}
                onDeleteExpense={deleteExpense}
                currency={currency}
                carId={selectedCarId}
                categoryFilter={categoryFilter}
                onCategoryFilterChange={setCategoryFilter}
                language={language}
                distanceUnit={distanceUnit}
                isAdding={activeTab === 'add-expense'}
                setIsAdding={(val) => {
                  if (val) setActiveTab('add-expense');
                  else setActiveTab('expenses');
                }}
                isPro={isPro}
                currentMileageKm={currentMileageKm}
              />
            </>
          );
        })()}

      </main>

      {/* Settings Modal */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowSettings(false)}>
              <X size={24} />
            </button>
            <Settings
              currency={currency}
              onCurrencyChange={setCurrency}
              language={language}
              onLanguageChange={handleLanguageChange}
              distanceUnit={distanceUnit}
              onDistanceUnitChange={setDistanceUnit}
              colorBlindMode={colorBlindMode}
              onColorBlindModeChange={setColorBlindMode}
              theme={theme}
              onThemeChange={setTheme}
              isPro={isPro}
              offerings={offerings}
              offeringsError={offeringsError}
              serviceProducts={serviceProducts}
              purchasing={purchasing}
              restoring={restoring}
              rcError={rcError}
              onClearRcError={() => setRcError(null)}
              onPurchasePackage={purchasePackage}
              onPurchaseServiceAccount={purchaseServiceAccount}
              onRestorePurchases={restorePurchases}
              onPresentPaywall={presentPaywall}
              onRedeemPromoCode={redeemPromoCode}
              onCancelSubscription={() => {
                if (isWeb) {
                  stripeManageSubscription();
                } else {
                  setIsPro(false);
                }
              }}
              onFetchOfferings={fetchOfferings}
              showServiceRegistration={SHOW_SERVICE_REGISTRATION}
              isServiceAccount={isServiceAccount}
              // Stripe (web only)
              isWeb={isWeb}
              onStripeCheckout={purchaseStripe}
              stripeError={stripeError}
              onClearStripeError={() => setStripeError(null)}
              stripePurchasing={stripePurchasing}
            />
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditProfile && (() => {
        const COLORS = ['#5558FF', '#0891B2', '#7C3AED', '#E11D48', '#FAF105', '#16A34A', '#D97706'];
        return (
          <div onClick={() => setShowEditProfile(false)} style={{
            position: 'fixed', inset: 0, zIndex: 500,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'flex-end',
          }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: 'var(--bg-primary)', width: '100%',
              borderRadius: '24px 24px 0 0', padding: '1.5rem 1.5rem 2.5rem',
              animation: 'slideUp 0.25s ease-out',
            }}>
              {/* Handle */}
              <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: 'var(--border-color)', margin: '0 auto 1.25rem' }} />

              {/* Avatar preview */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
                <div style={{
                  width: '72px', height: '72px', borderRadius: '50%',
                  background: editUsePhoto ? 'transparent' : editColor,
                  overflow: 'hidden', border: `3px solid ${editColor}`,
                  boxShadow: `0 6px 18px ${editColor}55`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.8rem', fontWeight: 700,
                  color: editColor === '#FAF105' ? '#1a1a1a' : 'white',
                }}>
                  {editUsePhoto && editPhoto
                    ? <img src={editPhoto} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : (editName ? editName[0].toUpperCase() : 'U')
                  }
                </div>
              </div>

              {/* Name input */}
              <input
                type="text"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                placeholder={t?.yourName || 'Your name…'}
                maxLength={24}
                style={{
                  width: '100%', padding: '0.85rem 1rem',
                  background: 'var(--bg-secondary)',
                  border: '1.5px solid var(--border-color)',
                  borderRadius: '14px', fontSize: '1rem',
                  color: 'var(--text-primary)', fontWeight: 600,
                  marginBottom: '1.25rem', boxSizing: 'border-box', outline: 'none',
                }}
                onFocus={e => e.target.style.borderColor = editColor}
                onBlur={e => e.target.style.borderColor = 'var(--border-color)'}
              />

              {/* Color + photo grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.65rem', marginBottom: '1.25rem' }}>
                {COLORS.map(color => {
                  const active = !editUsePhoto && editColor === color;
                  return (
                    <button key={color} onClick={() => { setEditColor(color); setEditUsePhoto(false); }} style={{
                      aspectRatio: '1', borderRadius: '50%', background: color, padding: 0, cursor: 'pointer',
                      border: active ? '3px solid white' : '3px solid transparent',
                      outline: active ? `3px solid ${color}` : 'none',
                      boxShadow: active ? `0 4px 12px ${color}88` : 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.2s',
                    }}>
                      {active && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color === '#FAF105' ? '#1a1a1a' : 'white'} strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                    </button>
                  );
                })}
                {/* Camera slot */}
                <button onClick={() => editFileRef.current.click()} style={{
                  aspectRatio: '1', borderRadius: '50%', padding: 0, cursor: 'pointer',
                  background: editUsePhoto && editPhoto ? 'transparent' : 'var(--bg-secondary)',
                  border: editUsePhoto ? `3px solid ${editColor}` : '3px solid var(--border-color)',
                  outline: editUsePhoto ? `3px solid ${editColor}` : 'none',
                  overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s',
                }}>
                  {editUsePhoto && editPhoto
                    ? <img src={editPhoto} alt="custom" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                  }
                </button>
              </div>
              <input ref={editFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onloadend = () => { setEditPhoto(reader.result); setEditUsePhoto(true); };
                reader.readAsDataURL(file);
              }} />

              {/* Save button */}
              <button
                onClick={() => {
                  const newName = editName.trim() || 'User';
                  const newPhoto = editUsePhoto ? editPhoto : null;
                  setProfileName(newName);
                  setProfileColor(editColor);
                  setProfilePhoto(newPhoto);
                  localStorage.setItem('profileName', newName);
                  localStorage.setItem('profileColor', editColor);
                  if (newPhoto) localStorage.setItem('profilePhoto', newPhoto);
                  else localStorage.removeItem('profilePhoto');
                  setShowEditProfile(false);
                }}
                style={{
                  width: '100%', padding: '0.9rem',
                  background: editColor, border: 'none', borderRadius: '14px',
                  color: editColor === '#FAF105' ? '#1a1a1a' : 'white',
                  fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
                }}
              >
                {t?.saveChanges || 'Save Changes'}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Car Setup Form */}
      {showSetupForm && (
        <CarSetupForm
          onComplete={handleSetupComplete}
          onCancel={cars.length > 0 ? () => {
            setShowSetupForm(false);
            setEditingCar(null);
          } : undefined}
          initialData={editingCar}
          language={language}
        />
      )}

      {/* Onboarding: Profile Setup (Step 1) */}
      {onboardingStep === 'profile' && (
        <ProfileSetup
          onComplete={({ name, color, photo }) => {
            setProfileName(name);
            setProfileColor(color);
            setProfilePhoto(photo || null);
            // Move to region step next
            setOnboardingStep(localStorage.getItem('region') ? 'done' : 'region');
          }}
        />
      )}

      {/* Onboarding: Region Selector (Step 2) */}
      {onboardingStep === 'region' && (
        <RegionSelector
          onSelect={(code) => {
            handleRegionSelect(code);
            setOnboardingStep('done');
          }}
          language={language}
        />
      )}

      {/* Maintenance Guide Popup */}
      {showMaintenanceGuidePopup && (
        <div className="modal-overlay" onClick={() => setShowMaintenanceGuidePopup(false)} style={{ alignItems: 'center', zIndex: 1000 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
            background: 'var(--modal-bg, var(--bg-primary))',
            color: 'var(--text-primary)',
            padding: '24px',
            borderRadius: '16px',
            maxWidth: '320px',
            width: '90%',
            textAlign: 'center',
            position: 'relative',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
          }}>
            <button className="modal-close" onClick={() => setShowMaintenanceGuidePopup(false)} style={{ color: 'var(--text-secondary)', top: '16px', right: '16px' }}>
              <X size={20} />
            </button>
            <div style={{
              width: '48px', height: '48px', margin: '0 auto 16px',
              background: 'rgba(234, 179, 8, 0.1)',
              borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <SettingsIcon size={24} color="#eab308" />
            </div>
            <h3 style={{ marginTop: '0', marginBottom: '16px', fontSize: '1.25rem', fontWeight: 'bold' }}>{t?.maintenanceGuide || 'Maintenance Guide'}</h3>
            <p style={{ margin: '0 0 24px 0', lineHeight: '1.5', color: 'var(--text-secondary)' }}>
              <strong style={{ color: '#eab308' }}>{t?.yellow || 'Yellow'}</strong>
              {' '}
              {t?.maintenanceGuideBodyMid || 'on the maintenance grid means recommended change and'}
              {' '}
              <strong style={{ color: '#ef4444' }}>{t?.red || 'red'}</strong>
              {' '}
              {t?.maintenanceGuideBodyEnd || 'means overdue.'}
            </p>
            <button
              className="save-btn"
              onClick={() => setShowMaintenanceGuidePopup(false)}
              style={{ width: '100%', padding: '12px', fontSize: '16px', borderRadius: '8px' }}
            >
              {t?.gotIt || 'Got it'}
            </button>
          </div>
        </div>
      )}

      {/* Random Promo Popup */}
      {showPromoPopup && !isPro && (
        <div className="modal-overlay" onClick={() => setShowPromoPopup(false)} style={{ alignItems: 'center' }}>
          <div className="modal-content pro-upgrade-modal" onClick={(e) => e.stopPropagation()} style={{
            textAlign: 'center',
            padding: 0,
            width: '90%',
            maxWidth: '420px',
            borderRadius: '28px',
            margin: '0 auto',
            animation: 'fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            background: 'var(--bg-primary)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Premium Header Banner */}
            <div style={{
              background: 'linear-gradient(180deg, #1f2235 0%, #151624 100%)',
              padding: '3rem 2rem 2rem',
              position: 'relative'
            }}>
              <button className="modal-close" onClick={() => setShowPromoPopup(false)} style={{ color: '#ffffff', background: 'rgba(255,255,255,0.1)', top: '16px', right: '16px', border: 'none', width: '32px', height: '32px', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <X size={20} />
              </button>
              <div style={{
                width: '72px', height: '72px', margin: '0 auto 1.5rem',
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid rgba(251, 191, 36, 0.6)',
                boxShadow: '0 0 30px rgba(251, 191, 36, 0.15)'
              }}>
                <SettingsIcon size={36} color="#fbbf24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
              </div>

              <h2 style={{ fontSize: '1.75rem', margin: '0 0 0.75rem', color: '#ffffff', fontWeight: '800', letterSpacing: '-0.02em' }}>
                {t?.promoTitle || 'Tired of manual entry?'}
              </h2>
              <p style={{ color: '#9ca3af', margin: 0, fontSize: '0.95rem', lineHeight: 1.5, padding: '0 0.5rem' }}>
                {t?.promoBody || 'Let CarTrack automatically log your miles in the background!'}
              </p>
            </div>

            {/* Content Area */}
            <div style={{ padding: '2rem' }}>

              {/* Pricing Options */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                {/* Monthly Card */}
                <button
                  onClick={() => {
                    setShowPromoPopup(false);
                    presentPaywall();
                  }}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    padding: '1.25rem 0.5rem',
                    background: 'var(--bg-secondary)',
                    border: '2px solid transparent',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                >
                  <span style={{ fontSize: '1.15rem', color: 'var(--text-primary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t?.monthly || 'Monthly'}
                  </span>
                </button>

                {/* Yearly Card (Highlighted) */}
                <button
                  onClick={() => {
                    setShowPromoPopup(false);
                    presentPaywall();
                  }}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    padding: '1.25rem 0.5rem',
                    background: 'var(--bg-primary)',
                    border: '2px solid #fbbf24',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    boxShadow: '0 4px 15px rgba(251, 191, 36, 0.1)'
                  }}
                >
                  <div style={{
                    position: 'absolute', top: '-1px', left: '-1px', right: '-1px',
                    background: 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)',
                    color: 'white', fontSize: '0.7rem', fontWeight: 800, padding: '0.3rem',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    borderTopLeftRadius: '15px', borderTopRightRadius: '15px'
                  }}>
                    {t?.bestValue || 'Best Value'}
                  </div>
                  <span style={{ fontSize: '1.15rem', color: '#d97706', fontWeight: 800, marginTop: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t?.yearly || 'Yearly'}
                  </span>
                </button>
              </div>

              <button
                onClick={() => setShowPromoPopup(false)}
                style={{
                  width: '100%', padding: '0.75rem', border: 'none', background: 'transparent',
                  color: 'var(--text-secondary)', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                {t?.noThanks || "No thanks, I'll do it manually"}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Web AdSense banner — shown to free users on web only */}
      {isWeb && !isPro && <WebAdBanner isPro={isPro} />}

      <BottomNav
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onAddClick={handleAddClick}
        t={t}
        isPro={isPro}
        isServiceAccount={isServiceAccount}
      />
    </div>
  );
}

export default App;
